import type { RumGlobal as RumPublicApi } from "@datadog/browser-rum";
import { datadogRum } from "@datadog/browser-rum";
import Service, { service } from "@ember/service";
import type { IntlService } from "ember-intl";
import type { PaginationDirection } from "parog-web/components/search-table/-private/footer";
import type FollowUpReason from "parog-web/models/follow-up-reason";
import type { TlsConsentStringEnum } from "parog-web/models/tls-consent-journal-event";
import type SemeiaSessionService from "./session";

export type TrackActionArgs<T extends TrackActionName> = [
  T,
  ActionParameters[T]
];

export type TrackActionName = keyof ActionParameters & string;

export type ActionFields = {
  page_number?: number;
  patient_id: string;
  follow_up_reason: FollowUpReason["id"];
  therapeutic_area: string | undefined;
  center_configurator_id: string;
  center_support_id: string | undefined;
  tls_status: boolean;
  button_name:
    | "Header - Créer patient"
    | "Création patient - Recherche par IPP"
    | "Création patient - Lire carte vitale"
    | "Création patient - Récupérer INS"
    | "Création patient - Annuler"
    | "Configuration Patient - Fermer"
    | "Profil médical - Fermer";
  tls_consent: TlsConsentStringEnum;
  patient_app_access: boolean | undefined;
  deactivation_reason: string;
  deactivation_date: Date;
  table_name: "liste_patients";
  total_pages?: number;
  direction?: PaginationDirection;
  research_consent: boolean;
  column_name: string;
  filter_type: string;
  number_results: number | undefined;
  search_criteria_types: string[] | undefined;
  search_criteria_count: number | undefined;
  measure_type: MeasureType;
  modal_name: "objet_suivi_non_precise";
  modal_action: "close" | "continue";
};

interface ActionParameters {
  "Tableau Page Naviguée": Pick<
    ActionFields,
    "table_name" | "page_number" | "direction" | "total_pages"
  >;
  "Filtre Tableau Appliqué": Pick<
    ActionFields,
    "table_name" | "column_name" | "filter_type" | "number_results"
  >;
  "Bouton Cliqué": Pick<ActionFields, "button_name">;
  "Recherche Patient Effectuée": Pick<
    ActionFields,
    "number_results" | "search_criteria_types" | "search_criteria_count"
  >;
  "Patient Créé": Pick<
    ActionFields,
    | "patient_id"
    | "therapeutic_area"
    | "center_configurator_id"
    | "center_support_id"
  >;
  "Configuration Patient Sauvegardée": Pick<
    ActionFields,
    | "patient_id"
    | "tls_status"
    | "tls_consent"
    | "patient_app_access"
    | "research_consent"
  >;
  "Profil Médical Sauvegardé": Pick<ActionFields, "patient_id">;
  "Objet Suivi Mis À Jour": Pick<
    ActionFields,
    "patient_id" | "therapeutic_area" | "follow_up_reason"
  >;
  "Consentement Télésurveillance Modifié": Pick<
    ActionFields,
    "patient_id" | "tls_consent"
  >;
  "Accès Application Patient Modifié": Pick<
    ActionFields,
    "patient_id" | "patient_app_access"
  >;
  "Télésurveillance Activée": Pick<
    ActionFields,
    "patient_id" | "therapeutic_area" | "follow_up_reason"
  >;
  "Télésurveillance Désactivée": Pick<
    ActionFields,
    | "patient_id"
    | "therapeutic_area"
    | "follow_up_reason"
    | "deactivation_reason"
    | "deactivation_date"
  >;
  "Bouton Switch Centres Clické": Pick<
    ActionFields,
    "patient_id" | "center_configurator_id" | "center_support_id"
  >;
  "Mesure Déclarée": Pick<ActionFields, "measure_type">;
  "Modale Affichée": Pick<ActionFields, "modal_name" | "patient_id">;
  "Modale Fermée": Pick<
    ActionFields,
    "modal_name" | "modal_action" | "patient_id"
  >;
}

export type ActionColumnName =
  | "nom"
  | "suivi_pour"
  | "centre(s)_de_soins"
  | "référents_médicaux"
  | "inclusion"
  | "dernière_consultation"
  | "contacts";

type MeasureType =
  | "abdominal-area"
  | "adverse-events"
  | "capillarity-blood-glucose"
  | "blood-pressure"
  | "crohn-survey"
  | "dp-cycle"
  | "eva"
  | "hemodialysis-session"
  | "ibd-disk-survey"
  | "mammography-and-mri"
  | "oxygene-saturation"
  | "rch-survey"
  | "respiratory-rate"
  | "stools-number"
  | "temperature"
  | "vems-demm"
  | "weight"
  | "feeling-evas";

export default class AnalyticsService extends Service {
  @service declare intl: IntlService;
  @service declare session: SemeiaSessionService;

  mark(...args: Parameters<RumPublicApi["addTiming"]>) {
    performance.mark(args[0]);
    datadogRum.addTiming(...args);
  }

  startRouterTracking() {
    // No-op: Matomo tracking has been removed
  }

  trackAction<T extends TrackActionName>([
    actionName,
    context
  ]: TrackActionArgs<T>) {
    datadogRum.addAction(actionName, this.translatedContext(context));
  }

  trackEvent(
    _category: string,
    _action: string,
    _name: string,
    _value?: string
  ) {
    // No-op: Matomo tracking has been removed
  }

  trackJeCreation(_je) {
    // No-op: Matomo tracking has been removed
  }

  trackPatientMeasurement(measure_type: MeasureType) {
    if (!this.session.patient) return;

    this.trackAction(["Mesure Déclarée", { measure_type }]);
  }

  private translatedContext<T extends TrackActionName>(
    context: ActionParameters[T]
  ) {
    if (!context) return undefined;

    const newContext = {};

    for (const [key, value] of Object.entries(context) as Array<
      [keyof ActionFields, ActionFields[keyof ActionFields]]
    >) {
      newContext[key] = this.translatedKey(key, value);
    }

    return newContext;
  }

  private translatedFollowUpReason(fur: FollowUpReason["id"]) {
    let translation: string;
    if (fur.startsWith("stage")) translation = "mrc";
    else if (fur.endsWith("generic"))
      translation = fur.split("-")[0] + " " + this.intl.t(fur);
    else translation = this.intl.t(fur);

    return (
      translation
        .replace(/:/g, "")
        .replace(/ +/g, "_")
        .toLowerCase()
        .normalize("NFD")
        // removes accents
        .replace(/[\u0300-\u036f]/g, "")
    );
  }

  private translatedKey<K extends keyof ActionFields>(
    key: K,
    value: ActionFields[K]
  ) {
    switch (key) {
      case "follow_up_reason":
        return this.translatedFollowUpReason(
          value as ActionFields["follow_up_reason"]
        );
      case "patient_id":
        return "Patient-" + value;
      case "tls_consent":
      case "tls_status":
      case "patient_app_access":
      case "research_consent":
        return this.intl.t(`analytics.${key}.${value}`);
      default:
        return value;
    }
  }
}
